import { Candidate, GeoIdentity, parse } from './contracts.mjs';
import { sameGeometry } from './protocol.mjs';

const unavailable = (code) => Object.assign(new Error(code), { code });
export function createInspectSession({
  binding,
  api,
  guard,
  clock = Date.now,
  id = () => globalThis.crypto.randomUUID(),
}) {
  let state = 'off';
  let generation = 0;
  let epoch = 0;
  let docGeneration = 0;
  let selected = [];
  let prepared = null;
  let preparation = 0;
  let reason = null;
  let lease = null;
  const pending = new Map();
  const resolved = new Map();
  const listeners = new Set();
  const sequence = { hover: 0, click: 0 };
  let rateStart = clock();
  let samples = 0;
  const snapshot = () =>
    Object.freeze({
      state,
      binding: { ...binding },
      targets: structuredClone(selected),
      prepared: prepared ? structuredClone(prepared) : null,
      reason,
      limit: 16,
      docGeneration,
      epoch,
    });
  let cached = snapshot();
  const emit = () => {
    cached = snapshot();
    for (const listener of listeners) listener();
  };
  const active = () => {
    if (state !== 'on') throw unavailable('inspect_inactive');
  };
  function invalidateGeometry(id) {
    if (id.docGeneration < docGeneration || id.epoch < epoch) throw unavailable('stale_geometry');
    if (id.docGeneration !== docGeneration || id.epoch !== epoch) {
      pending.clear();
      resolved.clear();
      prepared = null;
      preparation += 1;
      if (id.docGeneration !== docGeneration)
        selected = selected.map((target) => ({ ...target, status: 'stale' }));
      docGeneration = id.docGeneration;
      epoch = id.epoch;
      emit();
    }
  }
  async function setOn(on) {
    if (on && ['on', 'arming'].includes(state)) return;
    const token = ++generation;
    pending.clear();
    resolved.clear();
    prepared = null;
    if (!on) {
      state = 'off';
      emit();
      const old = lease;
      lease = null;
      if (old) await guard.release(old);
      return;
    }
    state = 'arming';
    reason = null;
    emit();
    try {
      const previous = lease;
      lease = null;
      if (previous) await guard.release(previous);
      if (token !== generation) return;
      const acquired = await guard.acquire(binding);
      if (token !== generation) {
        await guard.release(acquired);
        return;
      }
      lease = acquired;
      state = 'on';
      emit();
    } catch {
      if (token === generation) {
        state = 'unavailable';
        reason = 'read_only_guard_unavailable';
        emit();
      }
    }
  }
  function beginRequest(raw, kind) {
    active();
    const id = parse(GeoIdentity, raw);
    if (
      !['hover', 'click'].includes(kind) ||
      id.sessionId !== binding.sessionId ||
      id.revision !== binding.revisionId
    )
      throw unavailable('request_scope_mismatch');
    invalidateGeometry(id);
    if (clock() - rateStart >= 1000) {
      rateStart = clock();
      samples = 0;
    }
    if (++samples > 30) throw unavailable('request_rate_limit');
    for (const [key, request] of pending) if (request.kind === kind) pending.delete(key);
    if (kind === 'click') resolved.clear();
    pending.set(id.requestId, { id, kind, until: clock() + 2000, seq: ++sequence[kind] });
    return sequence[kind];
  }
  async function resolve(raw, rawId) {
    active();
    const candidate = parse(Candidate, raw);
    const id = parse(GeoIdentity, rawId);
    const request = pending.get(id.requestId);
    // Admit observed geometry within its deadline. Trusted resolution may take longer;
    // its answer must still match the current request, document, geometry and binding.
    if (!request || request.until < clock() || !sameGeometry(request.id, id)) return null;
    pending.delete(id.requestId);
    const token = generation;
    const response = await api.resolve({
      artifactId: binding.artifactId,
      revisionId: binding.revisionId,
      buildId: binding.buildId,
      targets: [{ pid: candidate.pid, instanceRef: candidate.instanceRef, scope: 'source-site' }],
    });
    if (
      !currentResponse({
        token,
        generation,
        request,
        sequence,
        id,
        docGeneration,
        epoch,
        state,
      })
    )
      return null;
    const target = response.targets[0];
    if (!boundTarget(target, candidate, response.current, binding)) return null;
    const value = {
      ...target,
      status: 'current',
      details: response.details?.find((detail) => detail.targetId === target.targetId) ?? null,
    };
    if (request.kind === 'click') resolved.set(target.targetId, value);
    return value;
  }
  function add(targetId, { replace = false } = {}) {
    active();
    const target = resolved.get(targetId);
    if (!target) throw unavailable('host_click_required');
    resolved.delete(targetId);
    prepared = null;
    generation += 1;
    const next = replace ? [] : selected.filter((item) => item.targetId !== targetId);
    if (next.length >= 16) throw unavailable('selection_limit');
    selected = [...next, target];
    emit();
  }
  function remove(targetId) {
    generation += 1;
    selected = selected.filter((target) => target.targetId !== targetId);
    prepared = null;
    emit();
  }
  function clear() {
    generation += 1;
    selected = [];
    prepared = null;
    resolved.clear();
    emit();
  }
  async function freeze(destination, cropRefs = []) {
    active();
    const token = generation;
    const result = await api.freeze({
      ...selectionInput(binding, selected),
      destination,
      cropRefs,
    });
    if (token !== generation) throw unavailable('stale_session');
    state = 'frozen';
    pending.clear();
    resolved.clear();
    emit();
    return result;
  }
  async function prepare(changes) {
    active();
    const token = generation;
    const request = ++preparation;
    prepared = null;
    emit();
    const result = await api.prepare({
      ...selectionInput(binding, selected),
      changes,
      idempotencyKey: id(),
    });
    if (token !== generation || request !== preparation || state !== 'on')
      throw unavailable('stale_session');
    prepared = result;
    emit();
    return result;
  }
  async function apply(operationId, hostIntent) {
    if (!prepared || prepared.operationId !== operationId)
      throw unavailable('prepared_operation_required');
    const token = generation;
    const result = await api.apply({
      operationId,
      operationDigest: prepared.operationDigest,
      hostIntent,
    });
    if (token !== generation) throw unavailable('stale_session');
    prepared = null;
    selected = selected.map((target) => ({ ...target, status: 'stale' }));
    state = 'unavailable';
    reason = 'revision_changed';
    emit();
    return result;
  }
  function invalidate(code = 'document_replaced') {
    generation += 1;
    pending.clear();
    resolved.clear();
    prepared = null;
    selected = selected.map((target) => ({ ...target, status: 'stale' }));
    state = 'unavailable';
    reason = code;
    emit();
  }
  return Object.freeze({
    getSnapshot: () => cached,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setOn,
    beginRequest,
    requestDescriptor: (requestId) => {
      const descriptor = pending.get(requestId);
      return descriptor ? structuredClone(descriptor) : null;
    },
    resolve,
    add,
    remove,
    clear,
    freeze,
    prepare,
    apply,
    cancel: () => {
      preparation += 1;
      prepared = null;
      emit();
    },
    undo: (input) => api.undo(input),
    invalidate,
    async dispose() {
      invalidate('disposed');
      listeners.clear();
      const old = lease;
      lease = null;
      if (old) await guard.release(old);
    },
  });
}
function boundTarget(target, candidate, current, binding) {
  return (
    target &&
    target.pid === candidate.pid &&
    current?.revisionId === binding.revisionId &&
    current?.buildId === binding.buildId
  );
}
function currentResponse({
  token,
  generation,
  request,
  sequence,
  id,
  docGeneration,
  epoch,
  state,
}) {
  return (
    token === generation &&
    request.seq === sequence[request.kind] &&
    id.docGeneration === docGeneration &&
    id.epoch === epoch &&
    state === 'on'
  );
}
function selectionInput(binding, selected) {
  if (!selected.length || selected.some((target) => target.status !== 'current'))
    throw unavailable('selection_not_current');
  return {
    artifactId: binding.artifactId,
    revisionId: binding.revisionId,
    buildId: binding.buildId,
    targets: selected.map((target) => ({
      pid: target.pid,
      instanceRef: target.renderedInstanceRef,
      scope: target.scope,
    })),
  };
}
